import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { CustomBreadcrumbs } from '@/widgets/CustomBreadcrumbs';
import { useGetTeacherTermsQuery, ITeacherTerm } from '@/entities/TeacherTerms';
import { useGetTeacherIntakesQuery } from '@/entities/TeacherIntakes';
import { SectionLoader } from '@/shared/ui/Loader';
import { ContainerTable } from '@/shared/ui/AppTable';
import { routerPath } from '@/shared/config/router';
import { getTeacherGroupBreadcrumbs } from '@/shared/lib/utils/getTeacherCoursesBreadcrumbs';
import { termsColumns } from '../../config/termsColumns';

interface GroupViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
}

export const GroupView = ({ courseId, intakeId, groupNumber }: GroupViewProps) => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data, isLoading, isError } = useGetTeacherTermsQuery({ intakeId });
  const { data: intakesData } = useGetTeacherIntakesQuery({ courseId, pageSize: 100 });
  const terms = useMemo(() => data?.terms ?? [], [data?.terms]);
  const termsCount = terms.length;

  const groupTitle = useMemo(() => {
    const intake = intakesData?.intakes?.find((i) => i.id === intakeId);
    const group = intake?.groups?.find((g) => g.number === groupNumber);
    return group?.title ?? null;
  }, [intakesData?.intakes, intakeId, groupNumber]);

  const breadcrumbs = useMemo(
    () =>
      getTeacherGroupBreadcrumbs(
        courseId,
        data?.course?.title ?? 'Course',
        groupNumber,
        groupTitle,
      ),
    [courseId, data?.course?.title, groupNumber, groupTitle],
  );

  const handleTermClick = (term: ITeacherTerm) => {
    const path = routerPath.teacherCourseGroupTerm
      .replace(':courseId', courseId.toString())
      .replace(':intakeId', intakeId.toString())
      .replace(':groupNumber', groupNumber.toString())
      .replace(':termId', term.id.toString());
    navigate(path);
  };

  return (
    <div className="flex flex-col gap-6 bg-white rounded-[24px] p-6 h-full min-h-0 flex-1">
      <CustomBreadcrumbs items={breadcrumbs} />

      <div className="flex items-end justify-between">
        <div className="inline-flex gap-x-2 items-end">
          <h2 className="text-[32px] font-medium">
            {groupTitle?.trim() ? groupTitle : `Group ${groupNumber}`}
          </h2>
          <p className="text-grey-secondary leading-[220%]">(found {termsCount} terms)</p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {isLoading ? (
          <SectionLoader />
        ) : isError ? (
          <div className="bg-white p-6 rounded-[24px]">
            <div className="text-red-tertiary text-center py-8">
              Error loading terms. Please try again later.
            </div>
          </div>
        ) : (
          <ContainerTable<ITeacherTerm>
            caption="Terms in the group"
            data={terms}
            columns={termsColumns({ onRowClick: handleTermClick, readOnly: true })}
            pagination={pagination}
            setPagination={setPagination as OnChangeFn<PaginationState>}
            hideTopActions
          />
        )}
      </div>
    </div>
  );
};
